import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Calendar, Share2 } from 'lucide-react';
import DOMPurify from 'dompurify';
import axios from 'axios';
import { Lightbox } from '@/components/blog/Lightbox';
import { BlogVideoPanel } from '@/components/blog/BlogVideoPanel';
import { BlogGallery } from '@/components/blog/BlogGallery';
import { AuthorBox } from '@/components/blog/AuthorBox';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const FALLBACK_POSTS = {
  'washed-away-documentary': {
    title: 'Washed Away',
    slug: 'washed-away-documentary',
    excerpt: 'A documentary following communities in the Northern Rivers rebuilding after the 2022 floods.',
    content: `
      <p>Washed Away tells the story of the devastating 2022 floods in the Northern Rivers region of Australia, and the incredible community that rose up to help each other.</p>

      <h2>The Story</h2>
      <p>When the floods hit Lismore and surrounding areas, the scale of destruction was overwhelming. But what stood out was the resilience of the people. They weren't waiting for help—they were helping each other.</p>

      <h2>The Film</h2>
      <p>We spent months embedded in the community, building trust and capturing authentic moments. The goal was to honour the stories of those affected and celebrate the heroes who emerged.</p>

      <h2>The Impact</h2>
      <p>50% of all profits go directly to Hands and Hearts, supporting the ongoing rebuild of flood-damaged homes.</p>
    `,
    featured_image: 'https://customer-assets.emergentagent.com/job_site-clone-platform/artifacts/tetfj4m6_Terry_FFH_photo_01.jpg',
    vimeo_id: '465589411',
    gallery_images: [],
    tags: ['Documentary', 'Washed Away'],
    created_at: '2022-07-04',
  },
  welcome: {
    title: 'Welcome to Terry Webster',
    slug: 'welcome',
    excerpt: 'A brief introduction to my work as a filmmaker, cinematographer and producer.',
    content: `
      <p>G'day, I'm Terry Webster.</p>

      <p>For the past 17 years, I've been behind the camera — shooting documentaries, commercial content, and everything in between.</p>

      <p>This site is a collection of that work. You'll find brand campaigns for clients like Husqvarna and Home Beautiful. TV shows like Man Eats Wild. And documentary projects like <em>Washed Away</em>, which follows communities in the Northern Rivers rebuilding after the 2022 floods.</p>

      <p>I'm based in Australia but work wherever the story takes me.</p>

      <p>If you've got a project in mind, get in touch. Always happy to chat.</p>

      <p>— Terry</p>
    `,
    featured_image: 'https://customer-assets.emergentagent.com/job_site-clone-platform/artifacts/eyoeieqm_Terry_aboutme_photo_02.jpg',
    vimeo_id: '409449474',
    gallery_images: [],
    tags: ['Introduction', 'About'],
    created_at: '2025-03-30',
  },
};

const getFallbackPost = (slug) => {
  if (slug === 'washed-away-documentary' || slug === 'washed-away-award-winning-feature-documentary') {
    return FALLBACK_POSTS['washed-away-documentary'];
  }
  return FALLBACK_POSTS.welcome;
};

const PROSE_CLASSES = `prose prose-invert prose-lg max-w-none
  prose-headings:font-bold prose-headings:tracking-tight prose-headings:text-white
  prose-h2:text-2xl prose-h2:mt-8 prose-h2:mb-4
  prose-h3:text-xl prose-h3:mt-6 prose-h3:mb-3
  prose-p:text-[#A1A1AA] prose-p:leading-relaxed prose-p:mb-4
  prose-a:text-[#007AFF] prose-a:no-underline hover:prose-a:underline
  prose-strong:text-white
  prose-ul:text-[#A1A1AA] prose-li:mb-2
  prose-blockquote:border-l-[#007AFF] prose-blockquote:text-[#A1A1AA] prose-blockquote:italic`;

const BlogHero = ({ post }) => (
  <section className="relative pt-32 pb-16 md:pt-40 md:pb-20">
    <div className="absolute inset-0 bg-cover bg-top" style={{ backgroundImage: `url(${post.featured_image})` }} />
    <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/80 to-[#050505]/60" />

    <div className="container-wide relative z-10">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <Link
          to="/blog"
          className="inline-flex items-center gap-2 text-[#A1A1AA] hover:text-white mb-8 transition-colors"
          data-testid="back-to-blog-link"
        >
          <ArrowLeft size={18} />
          Back to Blog
        </Link>
      </motion.div>

      {post.tags && post.tags.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="flex flex-wrap gap-2 mb-6"
        >
          {post.tags.map((tag) => (
            <span key={tag} className="text-xs text-[#007AFF] px-3 py-1 border border-[#007AFF]/30 bg-[#007AFF]/10">
              {tag}
            </span>
          ))}
        </motion.div>
      )}

      <motion.h1
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tighter mb-6 max-w-4xl"
      >
        {post.title}
      </motion.h1>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="flex items-center gap-6 text-[#A1A1AA]"
      >
        <div className="flex items-center gap-2">
          <Calendar size={16} />
          <span>
            {new Date(post.created_at).toLocaleDateString('en-AU', { year: 'numeric', month: 'long', day: 'numeric' })}
          </span>
        </div>
        <button className="flex items-center gap-2 hover:text-[#007AFF] transition-colors">
          <Share2 size={16} />
          Share
        </button>
      </motion.div>
    </div>
  </section>
);

export default function BlogPost({ legacySlug }) {
  const { slug: paramSlug } = useParams();
  const slug = legacySlug || paramSlug;
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const fetchPost = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/blog/${slug}`);
      setPost(response.data);
    } catch (error) {
      setPost(getFallbackPost(slug));
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    fetchPost();
  }, [fetchPost]);

  const openLightbox = (index) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const galleryLength = post?.gallery_images?.length || 0;
  const nextImage = () => setLightboxIndex((prev) => (prev + 1) % galleryLength);
  const prevImage = () => setLightboxIndex((prev) => (prev - 1 + galleryLength) % galleryLength);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-3xl font-bold mb-4">Post Not Found</h1>
          <Link to="/blog" className="text-[#007AFF] hover:underline">
            ← Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  const hasVideo = post.vimeo_id || post.youtube_id || (post.vimeo_ids && post.vimeo_ids.length > 0);

  return (
    <main data-testid="blog-post-page" className="bg-[#050505]">
      <Lightbox
        open={lightboxOpen}
        images={post.gallery_images}
        index={lightboxIndex}
        onClose={() => setLightboxOpen(false)}
        onPrev={prevImage}
        onNext={nextImage}
      />

      <BlogHero post={post} />

      <section className="py-16 md:py-24" data-testid="blog-post-content">
        <div className="container-wide">
          {hasVideo ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
              <div>
                <p className="text-xl md:text-2xl text-[#A1A1AA] leading-relaxed mb-8 border-l-4 border-[#007AFF] pl-6">
                  {post.excerpt}
                </p>
                <article
                  className={PROSE_CLASSES}
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(post.content) }}
                />
              </div>
              <BlogVideoPanel post={post} />
            </div>
          ) : (
            <div className="max-w-3xl mx-auto">
              <p className="text-xl md:text-2xl text-[#A1A1AA] leading-relaxed mb-12 border-l-4 border-[#007AFF] pl-6">
                {post.excerpt}
              </p>
              <article
                className={PROSE_CLASSES}
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(post.content) }}
              />
            </div>
          )}

          <BlogGallery images={post.gallery_images} onImageClick={openLightbox} />
          <AuthorBox />
        </div>
      </section>

      <section className="py-16 bg-[#0C0C0C] border-t border-[#27272A]">
        <div className="container-wide text-center">
          <h2 className="text-2xl font-bold mb-4">Want to read more?</h2>
          <Link to="/blog" className="btn-secondary inline-flex items-center gap-2">
            <ArrowLeft size={18} />
            Back to All Posts
          </Link>
        </div>
      </section>
    </main>
  );
}
